/**
 * App.js - Main CompareStock Application Component
 * 
 * This is the primary React component for CompareStock, handling:
 * - User input (ticker symbols)
 * - Data fetching orchestration
 * - Financial statement display (Income, Balance Sheet, Cash Flow)
 * - Valuation ratios display
 * - "Build Your View" custom snapshot feature
 * - PNG export functionality
 * 
 * State Management:
 * - Uses React hooks (useState, useEffect, useMemo) for state
 * - No external state management library (Redux, etc.)
 * - All state is ephemeral (not persisted between sessions)
 * 
 * Data Flow:
 * 1. User enters ticker(s) and clicks Compare
 * 2. App fetches SEC EDGAR data + Finnhub quotes via secAPI.js
 * 3. Data is processed and stored in component state
 * 4. UI renders comparison tables from processed data
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  getCompanyFacts, 
  extractStatementData, 
  formatValue, 
  calculateDivergence,
  getStockQuote,
  getCompanyProfile,
  extractValuationInputs,
  calculateValuationRatios,
  hasQuarterlyData
} from './secAPI';
import './App.css';

/**
 * ALL_METRICS - Master list of metrics available in "Build Your View"
 * 
 * Each metric has:
 * - id: Unique identifier (matches valuation object keys or is used for state)
 * - label: Display name shown in UI
 * - category: Grouping for organization (Valuation, Income, Balance, Cash Flow)
 * - description: Tooltip/search text
 * - source: 'valuation' (calculated) or 'income'/'balance'/'cashflow' (from SEC)
 * - concept: (for SEC data) The XBRL concept name to extract
 */
const ALL_METRICS = [
  // Valuation Ratios - Calculated from price + fundamentals
  { id: 'price', label: 'Stock Price', category: 'Valuation Ratios', description: 'Current market price per share', source: 'valuation' },
  { id: 'marketCap', label: 'Market Cap', category: 'Valuation Ratios', description: 'Total market value of shares', source: 'valuation' },
  { id: 'enterpriseValue', label: 'Enterprise Value', category: 'Valuation Ratios', description: 'Market cap plus debt minus cash', source: 'valuation' },
  { id: 'peRatio', label: 'P/E Ratio', category: 'Valuation Ratios', description: 'Price to earnings ratio', source: 'valuation' },
  { id: 'psRatio', label: 'P/S Ratio', category: 'Valuation Ratios', description: 'Price to sales ratio', source: 'valuation' },
  { id: 'pbRatio', label: 'P/B Ratio', category: 'Valuation Ratios', description: 'Price to book value ratio', source: 'valuation' },
  { id: 'evRevenue', label: 'EV/Revenue', category: 'Valuation Ratios', description: 'Enterprise value to revenue', source: 'valuation' },
  { id: 'evEbitda', label: 'EV/EBITDA', category: 'Valuation Ratios', description: 'Enterprise value to EBITDA', source: 'valuation' },
  { id: 'grossMargin', label: 'Gross Margin %', category: 'Valuation Ratios', description: 'Gross profit as percent of revenue', source: 'valuation' },
  { id: 'operatingMargin', label: 'Operating Margin %', category: 'Valuation Ratios', description: 'Operating income as percent of revenue', source: 'valuation' },
  { id: 'netMargin', label: 'Net Margin %', category: 'Valuation Ratios', description: 'Net income as percent of revenue', source: 'valuation' },
  { id: 'roe', label: 'ROE %', category: 'Valuation Ratios', description: 'Return on equity', source: 'valuation' },
  { id: 'roa', label: 'ROA %', category: 'Valuation Ratios', description: 'Return on assets', source: 'valuation' },
  { id: 'currentRatio', label: 'Current Ratio', category: 'Valuation Ratios', description: 'Current assets divided by current liabilities', source: 'valuation' },
  { id: 'debtToEquity', label: 'Debt/Equity', category: 'Valuation Ratios', description: 'Total debt to shareholders equity', source: 'valuation' },
  
  // Statement of Operations
  { id: 'revenue', label: 'Total Revenue', category: 'Statement of Operations', description: 'Total sales revenue', source: 'income', concept: 'RevenueFromContractWithCustomerExcludingAssessedTax' },
  { id: 'costOfRevenue', label: 'Cost of Revenue', category: 'Statement of Operations', description: 'Direct costs of goods sold', source: 'income', concept: 'CostOfGoodsAndServicesSold' },
  { id: 'grossProfit', label: 'Gross Profit', category: 'Statement of Operations', description: 'Revenue minus cost of revenue', source: 'income', concept: 'GrossProfit' },
  { id: 'researchDev', label: 'Research & Development', category: 'Statement of Operations', description: 'R&D expenses', source: 'income', concept: 'ResearchAndDevelopmentExpense' },
  { id: 'salesMarketing', label: 'Sales & Marketing', category: 'Statement of Operations', description: 'Sales and marketing expenses', source: 'income', concept: 'SellingAndMarketingExpense' },
  { id: 'generalAdmin', label: 'General & Administrative', category: 'Statement of Operations', description: 'G&A expenses', source: 'income', concept: 'GeneralAndAdministrativeExpense' },
  { id: 'operatingExpenses', label: 'Operating Expenses', category: 'Statement of Operations', description: 'Total operating expenses', source: 'income', concept: 'OperatingExpenses' },
  { id: 'operatingIncome', label: 'Operating Income', category: 'Statement of Operations', description: 'Income from operations', source: 'income', concept: 'OperatingIncomeLoss' },
  { id: 'netIncome', label: 'Net Income', category: 'Statement of Operations', description: 'Bottom line profit or loss', source: 'income', concept: 'NetIncomeLoss' },
  { id: 'eps', label: 'Earnings Per Share', category: 'Statement of Operations', description: 'Net income per share', source: 'income', concept: 'EarningsPerShareBasic' },
  
  // Balance Sheet
  { id: 'cash', label: 'Cash & Equivalents', category: 'Balance Sheet', description: 'Cash and cash equivalents', source: 'balance', concept: 'CashAndCashEquivalentsAtCarryingValue' },
  { id: 'accountsReceivable', label: 'Accounts Receivable', category: 'Balance Sheet', description: 'Money owed by customers', source: 'balance', concept: 'AccountsReceivableNetCurrent' },
  { id: 'inventory', label: 'Inventory', category: 'Balance Sheet', description: 'Goods held for sale', source: 'balance', concept: 'InventoryNet' },
  { id: 'currentAssets', label: 'Current Assets', category: 'Balance Sheet', description: 'Assets convertible to cash within a year', source: 'balance', concept: 'AssetsCurrent' },
  { id: 'totalAssets', label: 'Total Assets', category: 'Balance Sheet', description: 'Total assets owned', source: 'balance', concept: 'Assets' },
  { id: 'accountsPayable', label: 'Accounts Payable', category: 'Balance Sheet', description: 'Money owed to suppliers', source: 'balance', concept: 'AccountsPayableCurrent' },
  { id: 'currentLiabilities', label: 'Current Liabilities', category: 'Balance Sheet', description: 'Debts due within a year', source: 'balance', concept: 'LiabilitiesCurrent' },
  { id: 'longTermDebt', label: 'Long-Term Debt', category: 'Balance Sheet', description: 'Debt due after one year', source: 'balance', concept: 'LongTermDebtNoncurrent' },
  { id: 'totalLiabilities', label: 'Total Liabilities', category: 'Balance Sheet', description: 'Total debts and obligations', source: 'balance', concept: 'Liabilities' },
  { id: 'shareholdersEquity', label: 'Shareholders Equity', category: 'Balance Sheet', description: 'Net worth to shareholders', source: 'balance', concept: 'StockholdersEquity' },
  
  // Cash Flow
  { id: 'operatingCashFlow', label: 'Operating Cash Flow', category: 'Cash Flow', description: 'Cash from operations', source: 'cashflow', concept: 'NetCashProvidedByUsedInOperatingActivities' },
  { id: 'capex', label: 'Capital Expenditures', category: 'Cash Flow', description: 'Spending on fixed assets', source: 'cashflow', concept: 'PaymentsToAcquirePropertyPlantAndEquipment' },
  { id: 'investingCashFlow', label: 'Investing Cash Flow', category: 'Cash Flow', description: 'Cash from investing activities', source: 'cashflow', concept: 'NetCashProvidedByUsedInInvestingActivities' },
  { id: 'financingCashFlow', label: 'Financing Cash Flow', category: 'Cash Flow', description: 'Cash from financing activities', source: 'cashflow', concept: 'NetCashProvidedByUsedInFinancingActivities' },
  { id: 'netCashChange', label: 'Net Change in Cash', category: 'Cash Flow', description: 'Total change in cash position', source: 'cashflow', concept: 'CashCashEquivalentsRestrictedCashAndRestrictedCashEquivalentsPeriodIncreaseDecreaseIncludingExchangeRateEffect' },
];

function App() {
  const [ticker1, setTicker1] = useState('');
  const [ticker2, setTicker2] = useState('');
  const [ticker3, setTicker3] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [company1Data, setCompany1Data] = useState(null);
  const [company2Data, setCompany2Data] = useState(null);
  const [company3Data, setCompany3Data] = useState(null);
  
  const [valuation1, setValuation1] = useState(null);
  const [valuation2, setValuation2] = useState(null);
  const [valuation3, setValuation3] = useState(null);
  const [valuationLoading, setValuationLoading] = useState(false);
  
  const [statementType, setStatementType] = useState('income');
  const [periodType, setPeriodType] = useState('quarterly');
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  
  // Track which sections are collapsed (by concept name)
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  
  // Build Your View state
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [metricSearch, setMetricSearch] = useState('');
  const SNAPSHOT_MAX_METRICS = 10;
  
  const addMetric = (metric) => {
    if (selectedMetrics.length >= SNAPSHOT_MAX_METRICS) return;
    if (selectedMetrics.some(m => m.id === metric.id)) return;
    setSelectedMetrics(prev => [...prev, metric]);
  };
  
  const removeMetric = (metricId) => {
    setSelectedMetrics(prev => prev.filter(m => m.id !== metricId));
  };
  
  const clearMetrics = () => {
    setSelectedMetrics([]);
  };
  
  const isMetricSelected = (metricId) => {
    return selectedMetrics.some(m => m.id === metricId);
  };
  
  // Social handle and platform state (persisted in localStorage)
  const [socialHandle, setSocialHandle] = useState(() => {
    return localStorage.getItem('comparestock_handle') || '';
  });
  const [socialPlatform, setSocialPlatform] = useState(() => {
    return localStorage.getItem('comparestock_platform') || 'x';
  });
  
  // Persist handle and platform to localStorage
  useEffect(() => {
    localStorage.setItem('comparestock_handle', socialHandle);
  }, [socialHandle]);
  
  useEffect(() => {
    localStorage.setItem('comparestock_platform', socialPlatform);
  }, [socialPlatform]);
  
  // PNG export function
  const downloadPNG = async () => {
    const element = document.getElementById('snapshot-export-area');
    if (!element) return;
    
    // Dynamically load html2canvas from CDN
    if (!window.html2canvas) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      document.head.appendChild(script);
      await new Promise(resolve => script.onload = resolve);
    }
    
    try {
      // Hide remove buttons before capture
      const removeButtons = element.querySelectorAll('.remove-metric-btn');
      removeButtons.forEach(btn => btn.style.display = 'none');
      
      const canvas = await window.html2canvas(element, {
        backgroundColor: '#111',
        scale: 2, // Higher resolution
      });
      
      // Restore remove buttons after capture
      removeButtons.forEach(btn => btn.style.display = '');
      
      const link = document.createElement('a');
      link.download = `comparestock-${company1Data?.ticker || 'snapshot'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('PNG export failed:', err);
      // Restore buttons on error too
      const removeButtons = element.querySelectorAll('.remove-metric-btn');
      removeButtons.forEach(btn => btn.style.display = '');
      alert('Export failed. Please try again.');
    }
  };
  
  // Filter metrics based on search
  const filteredMetrics = useMemo(() => {
    if (!metricSearch.trim()) return ALL_METRICS;
    const search = metricSearch.toLowerCase();
    return ALL_METRICS.filter(m => 
      m.label.toLowerCase().includes(search) ||
      m.description.toLowerCase().includes(search) ||
      m.category.toLowerCase().includes(search)
    );
  }, [metricSearch]);
  
  // Group filtered metrics by category
  const groupedMetrics = useMemo(() => {
    const groups = {};
    filteredMetrics.forEach(m => {
      if (!groups[m.category]) groups[m.category] = [];
      groups[m.category].push(m);
    });
    return groups;
  }, [filteredMetrics]);
  
  // Helper to get a metric value from company facts
  const getMetricValue = (metric, companyData, valuationData) => {
    if (!metric) return null;
    
    // Valuation metrics come from valuation data
    if (metric.source === 'valuation') {
      return valuationData?.[metric.id] ?? null;
    }
    
    // Statement metrics: use extractStatementData for consistency with statement tabs
    // This ensures Build Your View shows the SAME values as the statement tabs
    if (!companyData?.facts) return null;
    
    const statementType = metric.source; // 'income', 'balance', 'cashflow'
    
    // Extract using the same function the statement tabs use
    // Use annual data first (matches valuation tab), fall back to quarterly
    let rows = extractStatementData(companyData.facts, statementType, 'annual');
    if (rows.length === 0) {
      rows = extractStatementData(companyData.facts, statementType, 'quarterly');
    }
    
    // Find the row matching this metric's concept or label
    const matchRow = rows.find(r => r.concept === metric.concept) ||
                     rows.find(r => r.label === metric.label);
    
    if (matchRow && matchRow.quarters && matchRow.quarters.length > 0) {
      return matchRow.quarters[0]?.value ?? null;
    }
    
    return null;
  };
  
  const toggleSection = (concept) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(concept)) {
        newSet.delete(concept);
      } else {
        newSet.add(concept);
      }
      return newSet;
    });
  };

  const handleCompare = async () => {
    if (!ticker1.trim()) {
      setError('Please enter at least one ticker symbol');
      return;
    }

    // Validate ticker format (alphanumeric, 1-10 characters)
    const validateTicker = (ticker) => {
      if (!ticker) return null;
      const cleaned = ticker.trim().toUpperCase();
      if (!/^[A-Z0-9]{1,10}$/.test(cleaned)) {
        throw new Error(`Invalid ticker format: ${ticker}`);
      }
      return cleaned;
    };

    let validTicker1, validTicker2, validTicker3;
    try {
      validTicker1 = validateTicker(ticker1);
      validTicker2 = ticker2.trim() ? validateTicker(ticker2) : null;
      validTicker3 = ticker3.trim() ? validateTicker(ticker3) : null;
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    setLoading(true);
    setError('');
    setCompany1Data(null);
    setCompany2Data(null);
    setCompany3Data(null);
    setValuation1(null);
    setValuation2(null);
    setValuation3(null);

    const hasTicker2 = validTicker2 !== null;
    const hasTicker3 = validTicker3 !== null;

    try {
      // Fetch company data and profiles from SEC EDGAR and Finnhub
      const fetchPromises = [
        getCompanyFacts(validTicker1),
        getCompanyProfile(validTicker1)
      ];
      if (hasTicker2) {
        fetchPromises.push(getCompanyFacts(validTicker2));
        fetchPromises.push(getCompanyProfile(validTicker2));
      }
      if (hasTicker3) {
        fetchPromises.push(getCompanyFacts(validTicker3));
        fetchPromises.push(getCompanyProfile(validTicker3));
      }
      
      const results = await Promise.all(fetchPromises);
      
      // Parse results based on what was fetched
      const facts1 = results[0];
      const profile1 = results[1];
      
      setCompany1Data({
        ticker: validTicker1,
        name: facts1.entityName,
        facts: facts1,
        industry: { id: profile1.industry?.toLowerCase().replace(/\s+/g, '-') || 'unknown', name: profile1.industry || 'Unknown Industry' }
      });
      
      if (hasTicker2) {
        const facts2 = results[2];
        const profile2 = results[3];
        setCompany2Data({
          ticker: validTicker2,
          name: facts2.entityName,
          facts: facts2,
          industry: { id: profile2.industry?.toLowerCase().replace(/\s+/g, '-') || 'unknown', name: profile2.industry || 'Unknown Industry' }
        });
        
        if (hasTicker3) {
          const facts3 = results[4];
          const profile3 = results[5];
          setCompany3Data({
            ticker: validTicker3,
            name: facts3.entityName,
            facts: facts3,
            industry: { id: profile3.industry?.toLowerCase().replace(/\s+/g, '-') || 'unknown', name: profile3.industry || 'Unknown Industry' }
          });
        }
      }
      
      setSelectedPeriodIndex(0);
      
      // Auto-detect if primary company has quarterly data
      // If not (e.g., foreign issuers filing only 20-F), switch to annual mode
      if (!hasQuarterlyData(facts1)) {
        setPeriodType('annual');
      }
      
      // Fetch stock quotes and calculate valuations
      // Fetch stock quotes from Finnhub and calculate valuation ratios
      setValuationLoading(true);
      try {
        const quotePromises = [getStockQuote(validTicker1)];
        if (hasTicker2) {
          quotePromises.push(getStockQuote(validTicker2));
        }
        if (hasTicker3) {
          quotePromises.push(getStockQuote(validTicker3));
        }
        
        const quotes = await Promise.all(quotePromises);
        const [quote1, quote2, quote3] = quotes;
        
        // Calculate valuation ratios using annual fundamentals + current price
        // Pass Finnhub market cap (in millions, convert to full number)
        const inputs1 = extractValuationInputs(facts1, 'annual');
        const finnhubMarketCap1 = profile1.marketCap ? profile1.marketCap * 1000000 : null;
        setValuation1(calculateValuationRatios(quote1.price, inputs1, finnhubMarketCap1));
        
        if (hasTicker2) {
          const inputs2 = extractValuationInputs(results[2], 'annual');
          const finnhubMarketCap2 = results[3].marketCap ? results[3].marketCap * 1000000 : null;
          setValuation2(calculateValuationRatios(quote2.price, inputs2, finnhubMarketCap2));

          if (hasTicker3 && quote3) {
            const inputs3 = extractValuationInputs(results[4], 'annual');
            const finnhubMarketCap3 = results[5].marketCap ? results[5].marketCap * 1000000 : null;
            setValuation3(calculateValuationRatios(quote3.price, inputs3, finnhubMarketCap3));
          }
        }
      } catch (quotErr) {
        console.error('Error fetching quotes:', quotErr);
        // Don't fail the whole comparison if quotes fail
      }
      setValuationLoading(false);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check modes (after data is loaded)
  const isSingleCompanyMode = company1Data && !company2Data && !company3Data;
  const hasTwoCompanies = company1Data && company2Data && !company3Data;
  const hasThreeCompanies = company1Data && company2Data && company3Data;

  // Extract statement data for all companies
  const statement1 = company1Data ? extractStatementData(company1Data.facts, statementType, periodType) : [];
  const statement2 = company2Data ? extractStatementData(company2Data.facts, statementType, periodType) : [];
  const statement3 = company3Data ? extractStatementData(company3Data.facts, statementType, periodType) : [];

  // Find common line items (or just use company1 for single mode)
  const commonRows = [];
  const seen = new Set(); // Track by label for semantic matching
  
  // Helper to add a row to commonRows
  const addRow = (sourceRow, stmt1, stmt2, stmt3) => {
    if (seen.has(sourceRow.label)) return;
    seen.add(sourceRow.label);
    
    // Find matching rows by LABEL (semantic match), not by concept (XBRL tag)
    const row1 = stmt1.find(r => r.label === sourceRow.label);
    const row2 = stmt2.find(r => r.label === sourceRow.label);
    const row3 = hasThreeCompanies ? stmt3.find(r => r.label === sourceRow.label) : null;
    
    commonRows.push({
      concept: sourceRow.concept,
      label: sourceRow.label,
      isHeader: sourceRow.isHeader,
      isBold: sourceRow.isBold,
      isPerShare: sourceRow.isPerShare,
      isShares: sourceRow.isShares,
      sectionId: sourceRow.sectionId,
      isChild: sourceRow.isChild,
      order: sourceRow.order,
      company1Quarters: row1 ? row1.quarters : null,
      company2Quarters: row2 ? row2.quarters : null,
      company3Quarters: row3 ? row3.quarters : null
    });
  };
  
  // Helper to check if a section has data in any of the statements
  const sectionHasData = (stmt, sectionId) => 
    stmt.some(r => r.sectionId === sectionId && !r.isHeader && r.quarters.length > 0);
  
  if (isSingleCompanyMode) {
    // Single company mode: just show all rows from company1
    for (const row of statement1) {
      if (!seen.has(row.isHeader ? row.concept : row.label)) {
        seen.add(row.isHeader ? row.concept : row.label);
        commonRows.push({
          concept: row.concept,
          label: row.label,
          isHeader: row.isHeader,
          isBold: row.isBold,
          isPerShare: row.isPerShare,
          isShares: row.isShares,
          sectionId: row.sectionId,
          isChild: row.isChild,
          order: row.order,
          company1Quarters: row.quarters,
          company2Quarters: null,
          company3Quarters: null
        });
      }
    }
  } else {
    // Multi-company mode: BIDIRECTIONAL merge
    // First pass: Add all rows from Company 1
    for (const row1 of statement1) {
      if (row1.isHeader) {
        // Only show header if section has data in at least one company
        const hasData = sectionHasData(statement1, row1.sectionId) || 
                        sectionHasData(statement2, row1.sectionId) ||
                        (hasThreeCompanies && sectionHasData(statement3, row1.sectionId));
        
        if (hasData && !seen.has(row1.concept)) {
          seen.add(row1.concept);
          commonRows.push({
            concept: row1.concept,
            label: row1.label,
            isHeader: true,
            isBold: false,
            isPerShare: false,
            isShares: false,
            sectionId: row1.sectionId,
            isChild: false,
            company1Quarters: [],
            company2Quarters: [],
            company3Quarters: null
          });
        }
        continue;
      }
      
      if (row1.quarters && row1.quarters.length > 0) {
        addRow(row1, statement1, statement2, statement3);
      }
    }
    
    // Second pass: Add rows from Company 2 that Company 1 doesn't have
    for (const row2 of statement2) {
      if (row2.isHeader) continue; // Headers already handled
      if (row2.quarters && row2.quarters.length > 0) {
        addRow(row2, statement1, statement2, statement3);
      }
    }
    
    // Third pass: Add rows from Company 3 that neither 1 nor 2 have
    if (hasThreeCompanies) {
      for (const row3 of statement3) {
        if (row3.isHeader) continue;
        if (row3.quarters && row3.quarters.length > 0) {
          addRow(row3, statement1, statement2, statement3);
        }
      }
    }
  }

  // Re-sort rows by bucket order so items from Pass 2/3 appear in correct position
  // Without this, Operating Income from HOOD appears below Net Income when XOM (primary) doesn't have it
  if (!isSingleCompanyMode) {
    commonRows.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }

  // Get available periods - MERGE from all companies
  // CRITICAL: Look at ALL rows, not just the first, since different concepts may have different time ranges
  // e.g., HOOD's RevenueFromContract only has 2021 data, but Revenues has 2025 data
  const getPeriodsFromStatement = (stmt) => {
    const dataRows = stmt.filter(r => !r.isHeader && r.quarters && r.quarters.length > 0);
    if (dataRows.length === 0) return [];
    
    // Collect ALL unique periods across ALL rows, pick the most recent set
    const allPeriods = new Set();
    dataRows.forEach(r => r.quarters.forEach(q => allPeriods.add(q.period)));
    
    // Sort descending and take the 4 most recent
    const sorted = Array.from(allPeriods).sort((a, b) => new Date(b) - new Date(a));
    return sorted.slice(0, 4);
  };
  
  const periods1 = getPeriodsFromStatement(statement1);
  const periods2 = getPeriodsFromStatement(statement2);
  const periods3 = getPeriodsFromStatement(statement3);
  
  // Merge and dedupe all periods, sort by date descending
  const allPeriodsSet = new Set([...periods1, ...periods2, ...periods3]);
  let availablePeriods;
  
  if (periodType === 'annual') {
    // Annual: deduplicate by year (keep most recent date for each year)
    const byYear = {};
    for (const p of allPeriodsSet) {
      const year = new Date(p).getFullYear();
      if (!byYear[year] || new Date(p) > new Date(byYear[year])) {
        byYear[year] = p;
      }
    }
    availablePeriods = Object.values(byYear).sort((a, b) => new Date(b) - new Date(a));
  } else {
    // Quarterly: deduplicate by Q#-Year (keep most recent date for each quarter)
    const byQuarter = {};
    for (const p of allPeriodsSet) {
      const d = new Date(p);
      const q = Math.floor(d.getMonth() / 3) + 1;
      const key = `${d.getFullYear()}-Q${q}`;
      if (!byQuarter[key] || new Date(p) > new Date(byQuarter[key])) {
        byQuarter[key] = p;
      }
    }
    availablePeriods = Object.values(byQuarter).sort((a, b) => new Date(b) - new Date(a));
  }
  
  const selectedPeriod = availablePeriods[selectedPeriodIndex];
  
  // Helper: find value in a quarters array by period
  // Frame-based extraction standardizes periods so exact match is primary
  const findValueByPeriod = (quarters, targetPeriod) => {
    if (!quarters || quarters.length === 0 || !targetPeriod) return null;
    
    // Exact match
    const exact = quarters.find(q => q.period === targetPeriod);
    if (exact) return exact.value;
    
    // Fallback: match by year for annual (different fiscal year ends)
    if (periodType === 'annual') {
      const targetYear = new Date(targetPeriod).getFullYear();
      const yearMatch = quarters.find(q => new Date(q.period).getFullYear() === targetYear);
      if (yearMatch) return yearMatch.value;
    }
    
    // Fallback for quarterly: match by same quarter of same year
    // e.g., target=2025-09-30 (Q3) should match a value at 2025-09-27 (also Q3)
    if (periodType !== 'annual') {
      const targetDate = new Date(targetPeriod);
      const targetQ = Math.floor(targetDate.getMonth() / 3) + 1;
      const targetYear = targetDate.getFullYear();
      const qMatch = quarters.find(q => {
        const d = new Date(q.period);
        return d.getFullYear() === targetYear && Math.floor(d.getMonth() / 3) + 1 === targetQ;
      });
      if (qMatch) return qMatch.value;
    }
    
    return null;
  };
  
  // Track which companies have data for the selected period
  const companyHasMatchingData = (stmt, targetPeriod) => {
    if (!targetPeriod) return false;
    const targetDate = new Date(targetPeriod);
    const targetQ = Math.floor(targetDate.getMonth() / 3) + 1;
    const targetYear = targetDate.getFullYear();
    
    return stmt.some(r => !r.isHeader && r.quarters?.length > 0 && 
      r.quarters.some(q => {
        if (q.value === null) return false;
        if (q.period === targetPeriod) return true;
        // Quarterly fuzzy: same quarter, same year
        if (periodType !== 'annual') {
          const d = new Date(q.period);
          return d.getFullYear() === targetYear && Math.floor(d.getMonth() / 3) + 1 === targetQ;
        }
        // Annual fuzzy: same year
        if (periodType === 'annual') {
          return new Date(q.period).getFullYear() === targetYear;
        }
        return false;
      }));
  };
  
  const company1Filed = companyHasMatchingData(statement1, selectedPeriod);
  const company2Filed = companyHasMatchingData(statement2, selectedPeriod);
  const company3Filed = companyHasMatchingData(statement3, selectedPeriod);

  // Get revenue for margin calculations - use LABEL matching, not concept
  const getRevenueValue = (rows, targetPeriod) => {
    const revenueRow = rows.find(r => r.label === 'Total Revenue');
    if (!revenueRow?.quarters) return null;
    return findValueByPeriod(revenueRow.quarters, targetPeriod);
  };

  const revenue1 = getRevenueValue(statement1, selectedPeriod);
  const revenue2 = getRevenueValue(statement2, selectedPeriod);

  const statementLabels = {
    income: 'Statement of Operations',
    balance: 'Balance Sheet',
    cashflow: 'Cash Flow Statement',
    valuation: 'Valuation Ratios',
    snapshot: 'Build Your View'
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <h1>CompareStock</h1>
        <p>Side-by-Side SEC Quarterly/Annual report comparison</p>
        <span style={{ fontSize: '10px', color: '#00dc82' }}>v2.2</span>
      </header>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-inputs">
          <input
            type="text"
            placeholder="Primary (e.g., TOST)"
            value={ticker1}
            onChange={(e) => setTicker1(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleCompare()}
            className="ticker-input"
            title="Primary ticker - others will be compared against this"
          />
          <span className="vs-text">vs</span>
          <input
            type="text"
            placeholder="Compare (e.g., DUOL)"
            value={ticker2}
            onChange={(e) => setTicker2(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleCompare()}
            className="ticker-input ticker-optional"
            title="Compare against primary (optional)"
          />
          <span className="vs-text">vs</span>
          <input
            type="text"
            placeholder="Reference (optional)"
            value={ticker3}
            onChange={(e) => setTicker3(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleCompare()}
            className="ticker-input ticker-optional"
            title="Optional reference ticker for additional context"
          />
          <button 
            onClick={handleCompare} 
            disabled={loading}
            className="compare-btn"
          >
            {loading ? 'Loading...' : 'Compare'}
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <p className="instructional-text">
          This tool provides a side-by-side comparison of consolidated SEC statements filed by US companies. View their financials by quarter or annually. Compare their statement of operations, balance sheets, cash flow statement and even valuation ratios to help you make smarter financial investments. Always verify with original SEC documents.
        </p>
      </div>

      {/* Results */}
      {company1Data && (
        <div className="results-section">
          {/* Controls Bar */}
          <div className="controls-bar">
            {/* Statement Type Tabs */}
            <div className="statement-tabs">
              {['income', 'balance', 'cashflow', 'valuation', 'snapshot'].map(type => (
                <button
                  key={type}
                  onClick={() => setStatementType(type)}
                  className={`tab-btn ${statementType === type ? 'active' : ''}`}
                >
                  {statementLabels[type]}
                  {type === 'snapshot' && selectedMetrics.length > 0 && (
                    <span className="snapshot-count">{selectedMetrics.length}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Period Type Toggle - hide for valuation and snapshot tabs */}
            {statementType !== 'valuation' && statementType !== 'snapshot' && (
              <div className="period-type-toggle">
                <button
                  onClick={() => { setPeriodType('quarterly'); setSelectedPeriodIndex(0); }}
                  className={`toggle-btn ${periodType === 'quarterly' ? 'active' : ''}`}
                >
                  Quarterly
                </button>
                <button
                  onClick={() => { setPeriodType('annual'); setSelectedPeriodIndex(0); }}
                  className={`toggle-btn ${periodType === 'annual' ? 'active' : ''}`}
                >
                  Annual
                </button>
              </div>
            )}

            {/* Period Selector - hide for valuation and snapshot tabs */}
            {statementType !== 'valuation' && statementType !== 'snapshot' && (
              <div className="period-selector">
                <label>Period:</label>
                <select 
                  value={selectedPeriodIndex} 
                  onChange={(e) => setSelectedPeriodIndex(Number(e.target.value))}
                  className="period-dropdown"
                >
                  {availablePeriods.map((period, idx) => {
                    // Convert date to quarter label
                    const d = new Date(period);
                    const month = d.getMonth(); // 0-indexed
                    const year = d.getFullYear();
                    let label;
                    if (periodType === 'annual') {
                      label = `FY ${year}`;
                    } else {
                      // Map month to quarter: Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec
                      const q = Math.floor(month / 3) + 1;
                      label = `Q${q} ${year}`;
                    }
                    return (
                      <option key={period} value={idx}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            {/* Q4 Notice for quarterly view */}
            {periodType === 'quarterly' && statementType !== 'valuation' && statementType !== 'snapshot' && (
              <span className="q4-notice" title="10-Q filings cover Q1, Q2, Q3 only. Q4 and full-year data are in the annual 10-K filing.">
                Q4 & FY in Annual view
              </span>
            )}
          </div>

          {/* Scrollable container for headers + data on mobile */}
          <div className={`table-scroll-container ${hasTwoCompanies ? 'two-company' : ''} ${hasThreeCompanies ? 'three-company' : ''} ${isSingleCompanyMode ? 'single-company' : ''}`}>
          
          {/* Company Headers - 2 company mode (not shown on valuation tab) */}
          {hasTwoCompanies && statementType !== 'valuation' && (
            <div className="company-header-row no-industry">
              <div className="metric-label-header"></div>
              <div className="company-header">
                <span className="company-ticker">{company1Data.ticker}</span>
                <span className="company-name">{company1Data.name}</span>
                {!company1Filed && selectedPeriod && (
                  <span className="not-filed-notice">Not yet filed</span>
                )}
              </div>
              <div className="company-header">
                <span className="company-ticker">{company2Data.ticker}</span>
                <span className="company-name">{company2Data.name}</span>
                {!company2Filed && selectedPeriod && (
                  <span className="not-filed-notice">Not yet filed</span>
                )}
              </div>
              <div className="divergence-header" title={`% difference of ${company1Data.ticker} vs ${company2Data.ticker}`}>
                Diff %
              </div>
            </div>
          )}

          {/* Company Headers - single company mode uses two-company layout with empty columns */}
          {isSingleCompanyMode && statementType !== 'valuation' && (
            <div className="company-header-row single-company no-industry">
              <div className="metric-label-header"></div>
              <div className="company-header">
                <span className="company-ticker">{company1Data.ticker}</span>
                <span className="company-name">{company1Data.name}</span>
              </div>
              <div className="company-header"></div>
              <div className="divergence-header"></div>
            </div>
          )}

          {/* Company Headers - 3 company mode (with Diff % for each vs primary, not shown on valuation tab) */}
          {hasThreeCompanies && statementType !== 'valuation' && (
            <div className="company-header-row three-company">
              <div className="metric-label-header"></div>
              <div className="company-header primary-company" title="Primary ticker - others compared against this">
                <span className="company-ticker">{company1Data.ticker}</span>
                <span className="company-name">{company1Data.name}</span>
                <span className="primary-badge">Primary</span>
                {!company1Filed && selectedPeriod && (
                  <span className="not-filed-notice">Not yet filed</span>
                )}
              </div>
              <div className="company-header">
                <span className="company-ticker">{company2Data.ticker}</span>
                <span className="company-name">{company2Data.name}</span>
                {!company2Filed && selectedPeriod && (
                  <span className="not-filed-notice">Not yet filed</span>
                )}
              </div>
              <div className="divergence-header" title={`% difference of ${company1Data.ticker} (primary) vs ${company2Data.ticker}`}>
                Diff %
              </div>
              <div className="company-header">
                <span className="company-ticker">{company3Data.ticker}</span>
                <span className="company-name">{company3Data.name}</span>
                {!company3Filed && selectedPeriod && (
                  <span className="not-filed-notice">Not yet filed</span>
                )}
              </div>
              <div className="divergence-header" title={`% difference of ${company1Data.ticker} (primary) vs ${company3Data.ticker}`}>
                Diff %
              </div>
            </div>
          )}

          {/* Statement Title */}
          <div className="statement-title">
            {statementLabels[statementType]}
            {statementType !== 'valuation' && (
              <span className="statement-subtitle">
                {periodType === 'annual' ? 'Fiscal Year' : 'Quarter'} ending {availablePeriods[selectedPeriodIndex] && 
                  new Date(availablePeriods[selectedPeriodIndex]).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric', 
                    year: 'numeric'
                  })
                }
              </span>
            )}
            {statementType === 'valuation' && (
              <span className="statement-subtitle">
                Ratios use latest Annual (10-K) data • Price & Market Cap from Finnhub
              </span>
            )}
          </div>

          {/* Valuation Ratios Table */}
          {statementType === 'valuation' && (
            <div className={`data-table valuation-table ${hasThreeCompanies ? 'three-company' : ''} ${isSingleCompanyMode ? 'single-company' : ''} ${hasTwoCompanies ? 'two-company' : ''}`}>
              {valuationLoading ? (
                <div className="no-data">Loading valuation data...</div>
              ) : !valuation1 || (!isSingleCompanyMode && !valuation2) || (hasThreeCompanies && !valuation3) ? (
                <div className="no-data">Unable to load stock quotes. Please try again.</div>
              ) : (
                <>
                  {/* Company Header Row for Valuation */}
                  <div className={`valuation-company-header ${isSingleCompanyMode ? 'single-company' : ''} ${hasThreeCompanies ? 'three-company' : ''}`}>
                    <div className="metric-label-header"></div>
                    <div className="company-header">
                      <span className="company-ticker">{company1Data.ticker}</span>
                      <span className="company-name">{company1Data.name}</span>
                      {valuation1?.fiscalYear && (
                        <span className="fiscal-year-notice">{valuation1.fiscalYear}</span>
                      )}
                    </div>
                    {isSingleCompanyMode && (
                      <>
                        <div className="company-header"></div>
                        <div className="divergence-header"></div>
                      </>
                    )}
                    {hasTwoCompanies && (
                      <>
                        <div className="company-header">
                          <span className="company-ticker">{company2Data.ticker}</span>
                          <span className="company-name">{company2Data.name}</span>
                          {valuation2?.fiscalYear && (
                            <span className="fiscal-year-notice">{valuation2.fiscalYear}</span>
                          )}
                        </div>
                        <div className="divergence-header">Diff %</div>
                      </>
                    )}
                    {hasThreeCompanies && (
                      <>
                        <div className="company-header">
                          <span className="company-ticker">{company2Data.ticker}</span>
                          <span className="company-name">{company2Data.name}</span>
                          {valuation2?.fiscalYear && (
                            <span className="fiscal-year-notice">{valuation2.fiscalYear}</span>
                          )}
                        </div>
                        <div className="divergence-header">Diff %</div>
                        <div className="company-header">
                          <span className="company-ticker">{company3Data.ticker}</span>
                          <span className="company-name">{company3Data.name}</span>
                          {valuation3?.fiscalYear && (
                            <span className="fiscal-year-notice">{valuation3.fiscalYear}</span>
                          )}
                        </div>
                        <div className="divergence-header">Diff %</div>
                      </>
                    )}
                  </div>

                  {/* Define all metrics in organized sections */}
                  {[
                    // Market Data
                    { section: 'Market Data' },
                    { label: 'Stock Price', key: 'price', format: 'price', colorLogic: 'neutral', tooltip: 'Current market price for one share', formula: 'Real-time quote' },
                    { label: 'Market Cap', key: 'marketCap', format: 'currency', colorLogic: 'neutral', tooltip: 'Total value of all outstanding shares', formula: 'Price × Shares' },
                    { label: 'Enterprise Value', key: 'enterpriseValue', format: 'currency', colorLogic: 'neutral', tooltip: 'Total cost to acquire the company including debt, minus cash', formula: 'Market Cap + Debt − Cash' },
                    
                    // Valuation Ratios
                    { section: 'Valuation Ratios' },
                    { label: 'P/E Ratio', key: 'peRatio', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Price-to-Earnings: years of earnings to pay back the stock price; lower = better value', formula: 'Price ÷ EPS' },
                    { label: 'P/S Ratio', key: 'psRatio', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Price-to-Sales: price relative to revenue, useful for unprofitable companies; lower = better value', formula: 'Market Cap ÷ Revenue' },
                    { label: 'P/B Ratio', key: 'pbRatio', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Price-to-Book: price relative to net assets; <1 may indicate undervaluation', formula: 'Market Cap ÷ Book Value' },
                    { label: 'P/FCF Ratio', key: 'pfcfRatio', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Price-to-Free-Cash-Flow: price relative to actual cash generated; lower = better value', formula: 'Market Cap ÷ FCF' },
                    { label: 'EV/Revenue', key: 'evRevenue', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Enterprise Value to Revenue: total company cost per dollar of sales; lower = cheaper', formula: 'EV ÷ Revenue' },
                    { label: 'EV/EBITDA', key: 'evEbitda', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Enterprise Value to EBITDA: years of operating profit to pay off company; lower = cheaper', formula: 'EV ÷ EBITDA' },
                    { label: 'EV/FCF', key: 'evFcf', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Enterprise Value to Free Cash Flow: years of cash generation to pay off company; lower = cheaper', formula: 'EV ÷ FCF' },
                    
                    // Yields
                    { section: 'Yields' },
                    { label: 'Earnings Yield', key: 'earningsYield', format: 'percent', colorLogic: 'higher-better', tooltip: 'Earnings as % of price (inverse of P/E); higher = better return potential', formula: 'Net Income ÷ Market Cap' },
                    { label: 'FCF Yield', key: 'fcfYield', format: 'percent', colorLogic: 'higher-better', tooltip: 'Free cash flow as % of price; higher = more cash generated per dollar invested', formula: 'FCF ÷ Market Cap' },
                    
                    // Per Share Metrics
                    { section: 'Per Share Metrics' },
                    { label: 'EPS', key: 'eps', format: 'pershare', colorLogic: 'higher-better', tooltip: 'Earnings Per Share: profit attributable to each share (TTM); higher = more profitable', formula: 'Net Income ÷ Shares' },
                    { label: 'Revenue/Share', key: 'revenuePerShare', format: 'pershare', colorLogic: 'higher-better', tooltip: 'Revenue generated per share; higher = more sales per share', formula: 'Revenue ÷ Shares' },
                    { label: 'Book Value/Share', key: 'bookValuePerShare', format: 'pershare', colorLogic: 'higher-better', tooltip: 'Net assets per share; what each share would be worth if liquidated', formula: 'Book Value ÷ Shares' },
                    { label: 'FCF/Share', key: 'fcfPerShare', format: 'pershare', colorLogic: 'higher-better', tooltip: 'Free Cash Flow per share; actual cash generated for each share', formula: 'FCF ÷ Shares' },
                    { label: 'Cash/Share', key: 'cashPerShare', format: 'pershare', colorLogic: 'higher-better', tooltip: 'Cash holdings per share; higher = more financial flexibility', formula: 'Cash ÷ Shares' },
                    
                    // Profitability
                    { section: 'Profitability' },
                    { label: 'Gross Margin', key: 'grossMargin', format: 'percent', colorLogic: 'higher-better', tooltip: 'Profit after direct costs as % of revenue; higher = better pricing power', formula: 'Gross Profit ÷ Revenue' },
                    { label: 'Operating Margin', key: 'operatingMargin', format: 'percent', colorLogic: 'higher-better', tooltip: 'Profit from operations as % of revenue; higher = more efficient operations', formula: 'Operating Income ÷ Revenue' },
                    { label: 'Net Margin', key: 'netMargin', format: 'percent', colorLogic: 'higher-better', tooltip: 'Net profit as % of revenue; higher = more of each dollar becomes profit', formula: 'Net Income ÷ Revenue' },
                    { label: 'ROE', key: 'roe', format: 'percent', colorLogic: 'higher-better', tooltip: 'Return on Equity: profit generated from shareholders\' investment; higher = better', formula: 'Net Income ÷ Book Value' },
                    { label: 'ROA', key: 'roa', format: 'percent', colorLogic: 'higher-better', tooltip: 'Return on Assets: profit generated from total assets; higher = better', formula: 'Net Income ÷ Total Assets' },
                    
                    // Financial Health
                    { section: 'Financial Health' },
                    { label: 'Debt/Equity', key: 'debtToEquity', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Debt-to-Equity: how much debt vs shareholder equity; >1 means more debt than equity', formula: 'Total Debt ÷ Book Value' },
                    { label: 'Current Ratio', key: 'currentRatio', format: 'ratio', colorLogic: 'higher-better', tooltip: 'Ability to pay short-term debts; >1 = can cover, <1 = potential liquidity risk', formula: 'Current Assets ÷ Current Liabilities' },
                    { label: 'Net Debt', key: 'netDebt', format: 'currency', colorLogic: 'lower-better', tooltip: 'Total debt minus cash; negative = more cash than debt (strong position)', formula: 'Total Debt − Cash' },
                    { label: 'Net Debt/EBITDA', key: 'netDebtToEbitda', format: 'ratio', colorLogic: 'lower-better', tooltip: 'Years of operating profit needed to pay off net debt; <3 is generally healthy', formula: 'Net Debt ÷ EBITDA' },
                  ].map((item, idx) => {
                    // Section headers
                    if (item.section) {
                      return (
                        <div key={item.section} className="data-row section-header">
                          <div className="section-title">{item.section}</div>
                        </div>
                      );
                    }
                    
                    const val1 = valuation1[item.key];
                    const val2 = valuation2 ? valuation2[item.key] : null;
                    const val3 = hasThreeCompanies && valuation3 ? valuation3[item.key] : null;
                    
                    const formatVal = (val) => {
                      if (val === null || val === undefined) return '—';
                      if (item.format === 'price') return `$${val.toFixed(2)}`;
                      if (item.format === 'pershare') return `$${val.toFixed(2)}`;
                      if (item.format === 'currency') {
                        const absVal = Math.abs(val);
                        const sign = val < 0 ? '-' : '';
                        if (absVal >= 1e12) return `${sign}$${(absVal / 1e12).toFixed(2)}T`;
                        if (absVal >= 1e9) return `${sign}$${(absVal / 1e9).toFixed(2)}B`;
                        if (absVal >= 1e6) return `${sign}$${(absVal / 1e6).toFixed(1)}M`;
                        return `${sign}$${absVal.toLocaleString()}`;
                      }
                      if (item.format === 'percent') return `${val.toFixed(1)}%`;
                      if (item.format === 'ratio') return val.toFixed(2);
                      return val;
                    };
                    
                    // Calculate divergence
                    const getDivergence = (v1, v2) => {
                      if (v1 === null || v2 === null || v2 === 0) return null;
                      return ((v1 - v2) / Math.abs(v2)) * 100;
                    };
                    
                    const divergence2 = getDivergence(val1, val2);
                    const divergence3 = hasThreeCompanies ? getDivergence(val1, val3) : null;
                    
                    // Color logic for divergence
                    const getDivergenceColor = (divergence) => {
                      if (divergence === null) return '';
                      if (item.colorLogic === 'neutral') return '';
                      if (item.colorLogic === 'lower-better') {
                        return divergence > 0 ? 'negative' : 'positive';
                      }
                      return divergence > 0 ? 'positive' : 'negative';
                    };
                    
                    return (
                      <div 
                        key={item.key}
                        className={`data-row valuation-row ${idx % 2 === 0 ? 'even' : 'odd'}`}
                      >
                        <div className="metric-label" title={item.tooltip}>
                          <span className="metric-name">{item.label}</span>
                          <span className="metric-formula">{item.formula}</span>
                        </div>
                        <div className="value-cell">{formatVal(val1)}</div>
                        {isSingleCompanyMode && (
                          <>
                            <div className="value-cell"></div>
                            <div className="divergence-cell"></div>
                          </>
                        )}
                        {hasTwoCompanies && (
                          <div className="value-cell">{formatVal(val2)}</div>
                        )}
                        {hasTwoCompanies && (
                          <div className={`divergence-cell ${getDivergenceColor(divergence2)}`}>
                            {divergence2 !== null ? (
                              <span>{divergence2 > 0 ? '+' : ''}{divergence2.toFixed(1)}%</span>
                            ) : '—'}
                          </div>
                        )}
                        {hasThreeCompanies && (
                          <div className="value-cell">{formatVal(val2)}</div>
                        )}
                        {hasThreeCompanies && (
                          <div className={`divergence-cell ${getDivergenceColor(divergence2)}`}>
                            {divergence2 !== null ? (
                              <span>{divergence2 > 0 ? '+' : ''}{divergence2.toFixed(1)}%</span>
                            ) : '—'}
                          </div>
                        )}
                        {hasThreeCompanies && (
                          <div className="value-cell">{formatVal(val3)}</div>
                        )}
                        {hasThreeCompanies && (
                          <div className={`divergence-cell ${getDivergenceColor(divergence3)}`}>
                            {divergence3 !== null ? (
                              <span>{divergence3 > 0 ? '+' : ''}{divergence3.toFixed(1)}%</span>
                            ) : '—'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* Financial Statements Data Table */}
          {statementType !== 'valuation' && statementType !== 'snapshot' && (
            <div className={`data-table statements-table ${hasThreeCompanies ? 'three-company' : ''} ${isSingleCompanyMode ? 'single-company' : ''} ${hasTwoCompanies ? 'two-company' : ''}`}>
              {commonRows.length === 0 ? (
                <div className="no-data">
                  {(() => {
                    if (periodType === 'quarterly') {
                      const noQ = [];
                      if (company1Data && !hasQuarterlyData(company1Data.facts)) noQ.push(company1Data.ticker);
                      if (company2Data && !hasQuarterlyData(company2Data.facts)) noQ.push(company2Data.ticker);
                      if (company3Data && !hasQuarterlyData(company3Data.facts)) noQ.push(company3Data.ticker);
                      if (noQ.length > 0) {
                        const tickers = noQ.join(', ');
                        const verb = noQ.length === 1 ? 'is a foreign issuer that doesn\'t file' : 'are foreign issuers that don\'t file';
                        return `${tickers} ${verb} quarterly earnings as an XBRL dataset. CompareStock uses the SEC free API which retrieves only XBRL format, so only annual reports for foreign issuers are available. Please switch to Annual view.`;
                      }
                    }
                    return 'No matching data found. Companies may use different accounting terms or one may not have filed recent reports.';
                  })()}
                </div>
              ) : (
                (() => {
                  // Process each row
                  const rowsToRender = commonRows.map((row, idx) => {
                    // Headers are clickable toggles
                    const isCollapsible = row.isHeader;
                    
                    // Child rows are hidden when their section is collapsed
                    const isHidden = row.isChild && 
                                     row.sectionId && 
                                     collapsedSections.has(row.sectionId);
                    
                    return { ...row, idx, isCollapsible, isHidden };
                  });
                  
                  return rowsToRender.filter(row => !row.isHidden).map((row, visibleIdx) => {
                    // Header rows (no data, just toggle)
                    if (row.isHeader) {
                      const isCollapsed = collapsedSections.has(row.sectionId);
                      return (
                        <div 
                          key={row.concept}
                          className={`data-row header-row collapsible ${visibleIdx % 2 === 0 ? 'even' : 'odd'}`}
                          onClick={() => toggleSection(row.sectionId)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="metric-label">
                            <span className="collapse-icon">{isCollapsed ? '▶' : '▼'}</span>
                            {row.label}
                          </div>
                          <div className="value-cell"></div>
                          {isSingleCompanyMode && <div className="value-cell"></div>}
                          {isSingleCompanyMode && <div className="divergence-cell"></div>}
                          {hasTwoCompanies && <div className="value-cell"></div>}
                          {hasTwoCompanies && <div className="divergence-cell"></div>}
                          {hasThreeCompanies && <div className="value-cell"></div>}
                          {hasThreeCompanies && <div className="divergence-cell"></div>}
                          {hasThreeCompanies && <div className="value-cell"></div>}
                          {hasThreeCompanies && <div className="divergence-cell"></div>}
                        </div>
                      );
                    }
                    
                    // Get values by matching period date, not positional index
                    // This handles companies with different fiscal years or different concept time ranges
                    const val1 = row.company1Quarters ? findValueByPeriod(row.company1Quarters, selectedPeriod) : null;
                    const val2 = row.company2Quarters ? findValueByPeriod(row.company2Quarters, selectedPeriod) : null;
                    const val3 = row.company3Quarters ? findValueByPeriod(row.company3Quarters, selectedPeriod) : null;
                    
                    const isExpenseMetric = row.concept.includes('Expense') || 
                                            row.concept.includes('Cost') ||
                                            row.concept === 'OperatingExpenses';
                    
                    const getDivergenceVsOther = (otherVal) => {
                      if (otherVal === null || val1 === null || otherVal === 0) return null;
                      return ((val1 - otherVal) / Math.abs(otherVal)) * 100;
                    };
                    
                    const divergence2 = (hasTwoCompanies || hasThreeCompanies) ? 
                      (hasThreeCompanies ? getDivergenceVsOther(val2) : calculateDivergence(val1, val2)) : null;
                    const divergence3 = hasThreeCompanies ? getDivergenceVsOther(val3) : null;
                    
                    const getDivergenceColor = (divergence) => {
                      if (divergence === null) return '';
                      if (isExpenseMetric) {
                        return divergence > 0 ? 'negative' : 'positive';
                      }
                      return divergence > 0 ? 'positive' : 'negative';
                    };
                    
                    return (
                      <div 
                        key={row.concept}
                        className={`data-row ${visibleIdx % 2 === 0 ? 'even' : 'odd'} ${row.isChild ? 'child-row' : ''}`}
                      >
                        <div className={`metric-label ${row.isBold ? 'bold' : ''}`}>
                          {row.label}
                        </div>
                        <div className="value-cell">
                          {formatValue(val1, { isPerShare: row.isPerShare, isShares: row.isShares })}
                        </div>
                        {isSingleCompanyMode && (
                          <>
                            <div className="value-cell"></div>
                            <div className="divergence-cell"></div>
                          </>
                        )}
                        {hasTwoCompanies && (
                          <div className="value-cell">
                            {formatValue(val2, { isPerShare: row.isPerShare, isShares: row.isShares })}
                          </div>
                        )}
                        {hasTwoCompanies && (
                          <div className={`divergence-cell ${getDivergenceColor(divergence2)}`}>
                            {divergence2 !== null ? `${divergence2 > 0 ? '+' : ''}${divergence2.toFixed(1)}%` : '—'}
                          </div>
                        )}
                        {hasThreeCompanies && (
                          <div className="value-cell">
                            {formatValue(val2, { isPerShare: row.isPerShare, isShares: row.isShares })}
                          </div>
                        )}
                        {hasThreeCompanies && (
                          <div className={`divergence-cell ${getDivergenceColor(divergence2)}`}>
                            {divergence2 !== null ? `${divergence2 > 0 ? '+' : ''}${divergence2.toFixed(1)}%` : '—'}
                          </div>
                        )}
                        {hasThreeCompanies && (
                          <div className="value-cell">
                            {formatValue(val3, { isPerShare: row.isPerShare, isShares: row.isShares })}
                          </div>
                        )}
                        {hasThreeCompanies && (
                          <div className={`divergence-cell ${getDivergenceColor(divergence3)}`}>
                            {divergence3 !== null ? `${divergence3 > 0 ? '+' : ''}${divergence3.toFixed(1)}%` : '—'}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()
              )}
            </div>
          )}
          
          {/* Build Your View Tab */}
          {statementType === 'snapshot' && (
            <div className="snapshot-container">
              {/* Search Bar */}
              <div className="metric-search-container">
                <input
                  type="text"
                  placeholder="Search metrics by keyword (e.g., revenue, debt, margin, P/E)..."
                  value={metricSearch}
                  onChange={(e) => setMetricSearch(e.target.value)}
                  className="metric-search-input"
                />
                <div className="snapshot-data-note">
                  Valuation Ratios use latest Annual (10-K) • Statement items use selected period
                </div>
              </div>
              
              <div className="snapshot-layout">
                {/* Left: Metric Picker */}
                <div className="metric-picker">
                  <div className="metric-picker-header">
                    <span>Available Metrics</span>
                    <span className="metric-picker-hint">{selectedMetrics.length}/{SNAPSHOT_MAX_METRICS} selected</span>
                  </div>
                  <div className="metric-picker-list">
                    {['Valuation Ratios', 'Statement of Operations', 'Balance Sheet', 'Cash Flow'].map(category => {
                      const categoryMetrics = groupedMetrics[category];
                      if (!categoryMetrics || categoryMetrics.length === 0) return null;
                      
                      return (
                        <div key={category} className="metric-picker-category">
                          <div className="metric-picker-category-header">{category}</div>
                          {categoryMetrics.map(metric => {
                            const isSelected = isMetricSelected(metric.id);
                            const canAdd = selectedMetrics.length < SNAPSHOT_MAX_METRICS;
                            
                            return (
                              <div 
                                key={metric.id} 
                                className={`metric-picker-item ${isSelected ? 'selected' : ''} ${!canAdd && !isSelected ? 'disabled' : ''}`}
                                onClick={() => {
                                  if (isSelected) {
                                    removeMetric(metric.id);
                                  } else if (canAdd) {
                                    addMetric(metric);
                                  }
                                }}
                              >
                                <span className="metric-picker-checkbox">{isSelected ? '✓' : '+'}</span>
                                <span className="metric-picker-label">{metric.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Right: Preview */}
                <div className="snapshot-preview">
                  <div className="snapshot-preview-header">
                    <span>Your View</span>
                    {selectedMetrics.length > 0 && (
                      <button className="clear-snapshot-btn" onClick={clearMetrics}>Clear All</button>
                    )}
                  </div>
                  
                  {selectedMetrics.length === 0 ? (
                    <div className="snapshot-empty">
                      <p>Select metrics from the list</p>
                    </div>
                  ) : (
                    <>
                      {/* Export Area - this is what gets captured as PNG */}
                      <div id="snapshot-export-area" className="snapshot-export-area">
                        {/* Preview Table */}
                        <div className="snapshot-table">
                          <div className="snapshot-table-header">
                            <div className="snapshot-metric-col">Metric</div>
                            <div className="snapshot-value-col">{company1Data?.ticker}</div>
                            {company2Data && <div className="snapshot-value-col">{company2Data?.ticker}</div>}
                            {company3Data && <div className="snapshot-value-col">{company3Data?.ticker}</div>}
                          </div>
                          
                          {['Valuation Ratios', 'Statement of Operations', 'Balance Sheet', 'Cash Flow'].map(category => {
                            const categoryMetrics = selectedMetrics.filter(m => m.category === category);
                            if (categoryMetrics.length === 0) return null;
                            
                            return (
                              <React.Fragment key={category}>
                                <div className="snapshot-category-row">{category}</div>
                                {categoryMetrics.map((metric, idx) => {
                                  // Get values using helper function
                                  const val1 = getMetricValue(metric, company1Data, valuation1);
                                  const val2 = getMetricValue(metric, company2Data, valuation2);
                                  const val3 = getMetricValue(metric, company3Data, valuation3);
                                  
                                  const formatVal = (val) => {
                                    if (val === null || val === undefined) return '—';
                                    if (typeof val !== 'number') return val;
                                    if (Math.abs(val) >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
                                    if (Math.abs(val) >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
                                    if (Math.abs(val) >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
                                    if (Math.abs(val) >= 1e3) return `$${(val / 1e3).toFixed(1)}K`;
                                    if (Math.abs(val) < 100 && Math.abs(val) > 0.01) return val.toFixed(2);
                                    return val.toLocaleString();
                                  };
                                  
                                  return (
                                    <div key={metric.id} className={`snapshot-row ${idx % 2 === 0 ? 'even' : 'odd'}`}>
                                      <div className="snapshot-metric-col">
                                        <button 
                                          className="remove-metric-btn export-hide"
                                          onClick={() => removeMetric(metric.id)}
                                        >×</button>
                                        {metric.label}
                                      </div>
                                      <div className="snapshot-value-col">{formatVal(val1)}</div>
                                      {company2Data && <div className="snapshot-value-col">{formatVal(val2)}</div>}
                                      {company3Data && <div className="snapshot-value-col">{formatVal(val3)}</div>}
                                    </div>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </div>
                        
                        {/* Footer with handle and watermark */}
                        <div className="snapshot-footer">
                          <div className="snapshot-handle">
                            {socialHandle && (
                              <>
                                <span className="handle-text">{socialHandle}</span>
                                <span className={`platform-icon ${socialPlatform}`}>
                                  {socialPlatform === 'x' && '𝕏'}
                                  {socialPlatform === 'reddit' && (
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                                    </svg>
                                  )}
                                  {socialPlatform === 'instagram' && (
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                                    </svg>
                                  )}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="snapshot-watermark">comparestock.us</div>
                        </div>
                      </div>
                      
                      {/* Controls - outside export area */}
                      <div className="snapshot-controls">
                        <div className="handle-input-group">
                          <input
                            type="text"
                            placeholder="@yourhandle"
                            value={socialHandle}
                            onChange={(e) => setSocialHandle(e.target.value)}
                            className="handle-input"
                          />
                          <div className="platform-selector">
                            <button 
                              className={`platform-btn ${socialPlatform === 'x' ? 'active' : ''}`}
                              onClick={() => setSocialPlatform('x')}
                              title="X (Twitter)"
                            >𝕏</button>
                            <button 
                              className={`platform-btn ${socialPlatform === 'reddit' ? 'active' : ''}`}
                              onClick={() => setSocialPlatform('reddit')}
                              title="Reddit"
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                                <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                              </svg>
                            </button>
                            <button 
                              className={`platform-btn ${socialPlatform === 'instagram' ? 'active' : ''}`}
                              onClick={() => setSocialPlatform('instagram')}
                              title="Instagram"
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <button className="download-png-btn" onClick={downloadPNG}>
                          Download PNG
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          
          </div>{/* End table-scroll-container */}

          {/* Footer */}
          <div className="table-footer">
            <span>Source: {statementType === 'valuation' ? 'Finnhub (price) + SEC EDGAR (fundamentals)' : 'SEC EDGAR XBRL Data'}</span>
            <span className="footer-links">
              <a 
                href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${company1Data.ticker}&type=10-Q`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {company1Data.ticker} Filings
              </a>
              {company2Data && (
                <>
                  {' • '}
                  <a 
                    href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${company2Data.ticker}&type=10-Q`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {company2Data.ticker} Filings
                  </a>
                </>
              )}
              {company3Data && (
                <>
                  {' • '}
                  <a 
                    href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${company3Data.ticker}&type=10-Q`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {company3Data.ticker} Filings
                  </a>
                </>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
